/**
 * Update Inventory page (/inventory/update). Stage the daily vendor files (Tekion +
 * HomeNet + vAuto + the 6 RapidRecon exports), see a 9-file checklist with guardrails
 * and an active-files list (who uploaded, row count, freshness), then run the update
 * with live per-stage progress. Replaces the old one-shot "Update Inventory" button.
 */
import React, { useCallback, useEffect, useRef, useState } from "react"
import { Link } from "react-router-dom"

import Layout from "../../components/Layout"
import DealerGroupRequiredNotice from "../../components/DealerGroupRequiredNotice"
import { useDealerGroup } from "../../context/DealerGroupContext"
import { THEME } from "../../config/theme"
import {
  InventoryDigestStatus,
  InventoryRunHistoryEntry,
  InventoryUpdateStatus,
  StagedFile,
  clearStagedInventoryFiles,
  deleteStagedInventoryFile,
  getInventoryDigestStatus,
  getInventoryUpdateHistory,
  getInventoryUpdateStatus,
  listStagedInventoryFiles,
  runInventoryUpdate,
  stageInventoryFiles,
} from "../../api/inventory"

const sleep = (ms: number) => new Promise((r) => setTimeout(r, ms))

function relativeTime(iso: string | null): string {
  if (!iso) return "—"
  const then = new Date(iso).getTime()
  if (Number.isNaN(then)) return "—"
  const mins = Math.round((Date.now() - then) / 60000)
  if (mins < 1) return "just now"
  if (mins < 60) return `${mins}m ago`
  const hrs = Math.round(mins / 60)
  if (hrs < 24) return `${hrs}h ago`
  return `${Math.round(hrs / 24)}d ago`
}

export default function UpdateInventoryPage() {
  return (
    <Layout>
      <UpdateInventoryContent />
    </Layout>
  )
}

function UpdateInventoryContent() {
  const { dealerGroupId } = useDealerGroup()
  const [status, setStatus] = useState<InventoryUpdateStatus | null>(null)
  const [files, setFiles] = useState<StagedFile[]>([])
  const [history, setHistory] = useState<InventoryRunHistoryEntry[]>([])
  const [uploadBusy, setUploadBusy] = useState(false)
  const [running, setRunning] = useState(false)
  const [runStatus, setRunStatus] = useState<InventoryDigestStatus | null>(null)
  const [note, setNote] = useState<{ text: string; error: boolean } | null>(null)
  const fileInputRef = useRef<HTMLInputElement | null>(null)
  const [dragging, setDragging] = useState(false)

  const loadAll = useCallback(async () => {
    if (dealerGroupId == null) return
    const [s, f, h] = await Promise.all([
      getInventoryUpdateStatus(dealerGroupId),
      listStagedInventoryFiles(dealerGroupId),
      getInventoryUpdateHistory(dealerGroupId),
    ])
    setStatus(s)
    setFiles(f)
    setHistory(h)
  }, [dealerGroupId])

  useEffect(() => {
    void loadAll().catch((e) => setNote({ text: e instanceof Error ? e.message : "Failed to load", error: true }))
  }, [loadAll])

  const stale = new Set(status?.stale_slots ?? [])

  const upload = async (selected: File[]) => {
    if (selected.length === 0 || dealerGroupId == null) return
    setUploadBusy(true)
    setNote(null)
    try {
      const result = await stageInventoryFiles(selected, dealerGroupId)
      await loadAll()
      if (result.rejected.length > 0) {
        setNote({ text: `Couldn't recognize: ${result.rejected.map((r) => r.filename).join(", ")}`, error: true })
      } else {
        setNote({ text: `Staged ${result.staged.length} file${result.staged.length === 1 ? "" : "s"}.`, error: false })
      }
    } catch (e) {
      setNote({ text: e instanceof Error ? e.message : "Upload failed", error: true })
    } finally {
      setUploadBusy(false)
    }
  }

  const onFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const selected = event.target.files ? Array.from(event.target.files) : []
    event.target.value = ""
    void upload(selected)
  }

  const onDrop = (event: React.DragEvent) => {
    event.preventDefault()
    setDragging(false)
    void upload(Array.from(event.dataTransfer.files))
  }

  const removeFile = async (slot: string) => {
    if (dealerGroupId == null) return
    await deleteStagedInventoryFile(slot, dealerGroupId)
    await loadAll()
  }

  const clearAll = async () => {
    if (dealerGroupId == null) return
    await clearStagedInventoryFiles(dealerGroupId)
    await loadAll()
  }

  const run = async () => {
    if (dealerGroupId == null || !status?.can_run) return
    setRunning(true)
    setRunStatus(null)
    setNote(null)
    try {
      const baseline = await getInventoryDigestStatus(dealerGroupId)
      await runInventoryUpdate(dealerGroupId)
      for (let i = 0; i < 800; i++) {
        await sleep(1500)
        const s = await getInventoryDigestStatus(dealerGroupId)
        setRunStatus(s)
        if (s.id != null && s.id !== baseline.id && s.status && s.status !== "running") break
      }
    } catch (e) {
      setNote({ text: e instanceof Error ? e.message : "Update failed", error: true })
    } finally {
      setRunning(false)
      await loadAll().catch(() => undefined)
    }
  }

  if (dealerGroupId == null) {
    return <DealerGroupRequiredNotice surface="updating inventory" />
  }

  return (
    <div style={pageStyle}>
      <Link to="/inventory/vehicles" style={backLinkStyle}>← Back to inventory</Link>
      <div style={titleStyle}>Update Inventory</div>

      {status && (
        <div style={bannerStyle(status.mode)}>
          {status.mode === "seed"
            ? "First load — this will seed the ledger from these files."
            : "Day-over-day update — this will diff against the current ledger and log changes."}
        </div>
      )}

      {/* Drop zone + physical upload button */}
      <div
        style={dropZoneStyle(dragging)}
        onDragOver={(e) => { e.preventDefault(); setDragging(true) }}
        onDragLeave={() => setDragging(false)}
        onDrop={onDrop}
      >
        <div style={{ fontSize: 14, color: THEME.colors.neutral.light.textSecondary }}>
          Drag the daily files here, or
        </div>
        <button style={uploadButtonStyle} disabled={uploadBusy} onClick={() => fileInputRef.current?.click()}>
          {uploadBusy ? "Uploading…" : "Upload files"}
        </button>
        <input ref={fileInputRef} type="file" accept=".csv,.xls,.xlsx" multiple onChange={onFileChange} style={{ display: "none" }} />
      </div>
      {note && <div style={{ ...noteStyle, color: note.error ? THEME.colors.semantic.danger : THEME.colors.semantic.success }}>{note.text}</div>}

      {/* Checklist */}
      <div style={sectionTitleStyle}>Files</div>
      <div style={checklistStyle}>
        {status?.slots.map((slot) => {
          const staged = files.find((f) => f.slot === slot.slot)
          return (
            <div key={slot.slot} style={checklistRowStyle}>
              <span style={{ color: slot.present ? THEME.colors.semantic.success : THEME.colors.neutral.light.textSecondary, fontWeight: 700, width: 16 }}>
                {slot.present ? "✓" : "○"}
              </span>
              <div style={{ flex: 1 }}>
                <span style={{ fontWeight: 600, color: THEME.colors.text }}>{slot.label}</span>
                {slot.required && <span style={requiredBadgeStyle}>required</span>}
                {stale.has(slot.slot) && <span style={staleBadgeStyle}>older than last run</span>}
                <div style={hintStyle}>{slot.hint}</div>
              </div>
              {staged && (
                <div style={fileMetaStyle}>
                  <div>{staged.row_count != null ? `${staged.row_count.toLocaleString()} rows` : ""}</div>
                  <div>{staged.uploaded_by ? `${staged.uploaded_by} · ${relativeTime(staged.uploaded_at)}` : relativeTime(staged.uploaded_at)}</div>
                </div>
              )}
              {staged && <button style={removeButtonStyle} onClick={() => void removeFile(slot.slot)}>Remove</button>}
            </div>
          )
        })}
      </div>

      {status && status.warnings.length > 0 && (
        <div style={warningsStyle}>
          {status.warnings.map((w, i) => <div key={i}>▲ {w}</div>)}
        </div>
      )}

      {/* Run */}
      <div style={runBarStyle}>
        <button style={runButtonStyle(!status?.can_run || running)} disabled={!status?.can_run || running} onClick={() => void run()}>
          {running ? "Updating…" : status?.mode === "seed" ? "Run initial load" : "Run update"}
        </button>
        {files.length > 0 && !running && (
          <button style={clearAllStyle} onClick={() => void clearAll()}>Clear all</button>
        )}
        {!status?.can_run && !running && (
          <span style={{ fontSize: 13, color: THEME.colors.semantic.danger }}>Stage a Tekion file to run.</span>
        )}
        {running && (
          <span style={{ fontSize: 13, color: THEME.colors.text }}>
            {runStatus?.stage ?? "Starting…"}
          </span>
        )}
      </div>

      {/* Last / current run result */}
      {runStatus && runStatus.status === "success" && !running && (
        <div style={resultStyle}>
          Done — {(runStatus.records_new ?? 0).toLocaleString()} new · {(runStatus.records_updated ?? 0).toLocaleString()} updated · {(runStatus.changes_logged ?? 0).toLocaleString()} changes.{" "}
          <Link to="/inventory/vehicles" style={linkStyle}>View inventory →</Link>
        </div>
      )}
      {runStatus && runStatus.status === "failed" && (
        <div style={{ ...resultStyle, color: THEME.colors.semantic.danger }}>Update failed: {runStatus.error_message ?? "unknown error"}</div>
      )}

      {/* Run history */}
      {history.length > 0 && (
        <>
          <div style={sectionTitleStyle}>Recent updates</div>
          <div style={historyStyle}>
            {history.map((run) => (
              <div key={run.id} style={historyRowStyle}>
                <span style={{ width: 90, color: THEME.colors.neutral.light.textSecondary }}>{relativeTime(run.completed_at ?? run.started_at)}</span>
                <span style={{ width: 70, fontWeight: 600, color: run.status === "success" ? THEME.colors.semantic.success : run.status === "failed" ? THEME.colors.semantic.danger : THEME.colors.neutral.light.textSecondary }}>{run.status}</span>
                {/* Usernames are emails in production — truncate with an ellipsis instead
                    of overflowing the fixed column and painting over the counts. */}
                <span
                  title={run.triggered_by ?? undefined}
                  style={{ maxWidth: 170, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}
                >
                  {run.triggered_by ?? "—"}
                </span>
                <span style={{ color: THEME.colors.text }}>
                  {run.status === "success"
                    ? `${(run.records_new ?? 0).toLocaleString()} new · ${(run.records_updated ?? 0).toLocaleString()} updated · ${(run.changes_logged ?? 0).toLocaleString()} changes`
                    : run.error_message ?? ""}
                </span>
              </div>
            ))}
          </div>
        </>
      )}
    </div>
  )
}

// --- Styles ---

const pageStyle: React.CSSProperties = { padding: THEME.spacing.lg, fontFamily: THEME.fonts.primary, maxWidth: 820 }
const backLinkStyle: React.CSSProperties = { display: "inline-block", marginBottom: THEME.spacing.md, fontSize: 13, color: THEME.colors.navy, textDecoration: "none" }
const titleStyle: React.CSSProperties = { fontSize: 22, fontWeight: 700, color: THEME.colors.text, marginBottom: THEME.spacing.md }

const bannerStyle = (mode: string): React.CSSProperties => ({
  padding: `${THEME.spacing.sm} ${THEME.spacing.md}`,
  borderRadius: THEME.borderRadius.md,
  fontSize: 13,
  marginBottom: THEME.spacing.md,
  background: mode === "seed" ? THEME.colors.semantic.warning : THEME.colors.navy,
  color: THEME.colors.white,
})

const dropZoneStyle = (dragging: boolean): React.CSSProperties => ({
  display: "flex", flexDirection: "column", alignItems: "center", gap: THEME.spacing.sm,
  padding: THEME.spacing.lg,
  border: `2px dashed ${dragging ? THEME.colors.navy : THEME.colors.neutral.light.border}`,
  borderRadius: THEME.borderRadius.md,
  background: dragging ? THEME.colors.neutral.light.surface : "transparent",
})

const uploadButtonStyle: React.CSSProperties = {
  padding: `${THEME.spacing.xs} ${THEME.spacing.md}`, fontSize: 13, fontWeight: 500,
  borderRadius: THEME.borderRadius.md, border: "none", background: THEME.colors.navy, color: THEME.colors.white, cursor: "pointer", fontFamily: THEME.fonts.primary,
}

const noteStyle: React.CSSProperties = { fontSize: 13, marginTop: THEME.spacing.sm }

const sectionTitleStyle: React.CSSProperties = {
  fontSize: 13, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.5px", color: THEME.colors.navy,
  borderBottom: `1px solid ${THEME.colors.neutral.light.border}`, paddingBottom: THEME.spacing.xs,
  margin: `${THEME.spacing.lg} 0 ${THEME.spacing.md}`,
}

const checklistStyle: React.CSSProperties = { display: "flex", flexDirection: "column", gap: THEME.spacing.xs }
const checklistRowStyle: React.CSSProperties = {
  display: "flex", alignItems: "center", gap: THEME.spacing.sm,
  padding: `${THEME.spacing.xs} ${THEME.spacing.sm}`, borderBottom: `1px solid ${THEME.colors.neutral.light.border}`,
}
const requiredBadgeStyle: React.CSSProperties = { marginLeft: THEME.spacing.xs, fontSize: 10, color: THEME.colors.semantic.danger, textTransform: "uppercase", fontWeight: 700 }
const staleBadgeStyle: React.CSSProperties = { marginLeft: THEME.spacing.xs, fontSize: 10, color: THEME.colors.semantic.warning, textTransform: "uppercase", fontWeight: 700 }
const hintStyle: React.CSSProperties = { fontSize: 11, color: THEME.colors.neutral.light.textSecondary }
const fileMetaStyle: React.CSSProperties = { fontSize: 11, color: THEME.colors.neutral.light.textSecondary, textAlign: "right", minWidth: 130 }
const removeButtonStyle: React.CSSProperties = { background: "transparent", border: "none", color: THEME.colors.semantic.danger, fontSize: 12, cursor: "pointer", fontFamily: THEME.fonts.primary }

const warningsStyle: React.CSSProperties = { marginTop: THEME.spacing.md, fontSize: 12, color: THEME.colors.semantic.warning, display: "flex", flexDirection: "column", gap: 2 }

const runBarStyle: React.CSSProperties = { display: "flex", alignItems: "center", gap: THEME.spacing.md, marginTop: THEME.spacing.lg }
const runButtonStyle = (disabled: boolean): React.CSSProperties => ({
  padding: `${THEME.spacing.sm} ${THEME.spacing.lg}`, fontSize: 14, fontWeight: 600,
  borderRadius: THEME.borderRadius.md, border: "none",
  background: disabled ? THEME.colors.neutral.light.border : THEME.colors.navy,
  color: THEME.colors.white, cursor: disabled ? "not-allowed" : "pointer", fontFamily: THEME.fonts.primary,
})
const clearAllStyle: React.CSSProperties = { background: "transparent", border: "none", color: THEME.colors.neutral.light.textSecondary, fontSize: 13, cursor: "pointer", fontFamily: THEME.fonts.primary }
const resultStyle: React.CSSProperties = { marginTop: THEME.spacing.md, fontSize: 14, color: THEME.colors.semantic.success }
const linkStyle: React.CSSProperties = { color: THEME.colors.navy, textDecoration: "none" }

const historyStyle: React.CSSProperties = { display: "flex", flexDirection: "column", gap: THEME.spacing.xs, fontSize: 13 }
const historyRowStyle: React.CSSProperties = { display: "flex", alignItems: "baseline", gap: THEME.spacing.sm, padding: `${THEME.spacing.xs} 0`, borderBottom: `1px solid ${THEME.colors.neutral.light.border}` }
